/**
 * What a notification says.
 *
 * Screen copy goes through i18n in the app, but a message composed on a server
 * has no app to ask, so the copy lives here where both sides can read it and
 * where it can be tested. The language comes off the resident's profile for a
 * push, and off the screen for the inbox, which stores what happened rather
 * than the words, so a resident who switches language reads it in the new one.
 *
 * The rules of the copy are the same as everywhere else: plain, specific, and
 * never telling a resident they are paid up before an accountant has said so.
 */

import { type Birr, formatBirr } from "./money.js";
import { type Period, formatPeriod } from "./period.js";

export type Language = "en" | "am";

export const LANGUAGES: Language[] = ["en", "am"];

export function isLanguage(value: unknown): value is Language {
  return value === "en" || value === "am";
}

export interface Message {
  title: string;
  body: string;
}

export type Notification =
  | { kind: "paymentVerified"; amount: Birr; months: Period[] }
  | { kind: "paymentRejected"; amount: Birr; reason: string }
  /** `note` is the accountant's own words, when they added any. */
  | { kind: "rentDue"; amount: Birr; months: number; note?: string }
  | { kind: "repairAttended"; description: string }
  | { kind: "noticePosted"; title: string }
  | { kind: "urgentNotice"; title: string };

export const NOTIFICATION_KINDS = [
  "paymentVerified",
  "paymentRejected",
  "rentDue",
  "repairAttended",
  "noticePosted",
  "urgentNotice",
] as const satisfies readonly Notification["kind"][];

/** The longest message an accountant can add to a reminder. */
export const REMINDER_NOTE_LIMIT = 500;

const TITLES: Record<Language, Record<Notification["kind"], string>> = {
  en: {
    paymentVerified: "Payment verified",
    paymentRejected: "Payment rejected",
    rentDue: "Payment reminder",
    repairAttended: "Confirm repair",
    noticePosted: "Notice from the committee",
    urgentNotice: "Urgent notice from the committee",
  },
  am: {
    paymentVerified: "ክፍያው ተረጋግጧል",
    paymentRejected: "ክፍያው ውድቅ ሆኗል",
    rentDue: "የክፍያ አስታዋሽ",
    repairAttended: "ጥገናውን ያረጋግጡ",
    noticePosted: "ከኮሚቴው ማስታወቂያ",
    urgentNotice: "ከኮሚቴው አስቸኳይ ማስታወቂያ",
  },
};

function englishBody(notification: Notification): string {
  switch (notification.kind) {
    case "paymentVerified": {
      const months = notification.months.map((period) => formatPeriod(period, "latin"));
      const covered =
        months.length === 0
          ? "No month was fully paid. Check your account for details."
          : `Covers ${months.join(", ")}.`;
      return `${formatBirr(notification.amount, "latin")} verified. ${covered}`;
    }
    case "paymentRejected":
      return `${formatBirr(notification.amount, "latin")} payment rejected. ${notification.reason}`;
    case "rentDue": {
      const owed =
        notification.months === 1
          ? `${formatBirr(notification.amount, "latin")} outstanding for 1 month.`
          : `${formatBirr(notification.amount, "latin")} outstanding for ${String(notification.months)} months.`;
      return withNote(owed, notification.note, "From the accountant:");
    }
    case "repairAttended":
      return `${notification.description} Open Liyu to confirm whether the repair is complete.`;
    case "noticePosted":
    case "urgentNotice":
      return notification.title;
  }
}

function amharicBody(notification: Notification): string {
  switch (notification.kind) {
    case "paymentVerified": {
      const months = notification.months.map((period) => formatPeriod(period, "am"));
      const covered =
        months.length === 0
          ? "ሙሉ የተከፈለ ወር የለም። ዝርዝሩን በሒሳብዎ ይመልከቱ።"
          : `${months.join("፣ ")}ን ይሸፍናል።`;
      return `${formatBirr(notification.amount, "am")} ተረጋግጧል። ${covered}`;
    }
    case "paymentRejected":
      return `${formatBirr(notification.amount, "am")} ክፍያ ውድቅ ሆኗል። ${notification.reason}`;
    case "rentDue":
      return withNote(
        `${formatBirr(notification.amount, "am")} ለ${String(notification.months)} ወር ያልተከፈለ።`,
        notification.note,
        "ከሒሳብ ሠራተኛው፦",
      );
    case "repairAttended":
      return `${notification.description} ጥገናው መጠናቀቁን በልዩ ውስጥ ያረጋግጡ።`;
    case "noticePosted":
    case "urgentNotice":
      return notification.title;
  }
}

/** The accountant's own words, on a line of their own under the figures. */
function withNote(text: string, note: string | undefined, from: string): string {
  const said = note?.trim() ?? "";
  return said === "" ? text : `${text}\n\n${from} ${said}`;
}

/** The message, in the reader's language. */
export function messageFor(notification: Notification, language: Language = "en"): Message {
  return {
    title: TITLES[language][notification.kind],
    body: language === "am" ? amharicBody(notification) : englishBody(notification),
  };
}

/**
 * A notification read back off a document, or null for one this build cannot
 * read, so one odd row is skipped rather than taking the inbox down.
 */
export function toNotification(value: unknown): Notification | null {
  if (typeof value !== "object" || value === null) return null;
  const data = value as Record<string, unknown>;
  const amount = typeof data.amount === "number" ? data.amount : null;
  const text = (name: string) => (typeof data[name] === "string" ? (data[name] as string) : null);
  switch (data.kind) {
    case "paymentVerified":
      return amount !== null && Array.isArray(data.months)
        ? {
            kind: "paymentVerified",
            amount,
            months: data.months.filter((m): m is Period => typeof m === "string"),
          }
        : null;
    case "paymentRejected":
      return amount !== null && text("reason") !== null
        ? { kind: "paymentRejected", amount, reason: text("reason") ?? "" }
        : null;
    case "rentDue": {
      if (amount === null || typeof data.months !== "number") return null;
      const note = text("note");
      return { kind: "rentDue", amount, months: data.months, ...(note ? { note } : {}) };
    }
    case "repairAttended":
      return text("description") !== null
        ? { kind: "repairAttended", description: text("description") ?? "" }
        : null;
    case "noticePosted":
    case "urgentNotice":
      return text("title") !== null ? { kind: data.kind, title: text("title") ?? "" } : null;
    default:
      return null;
  }
}

/** One notification as a person's inbox keeps it. */
export interface InboxItem {
  id: string;
  notification: Notification;
  createdAt: Date | null;
  /** Null until the person has opened it. */
  readAt: Date | null;
}

function dateOf(value: unknown): Date | null {
  if (value instanceof Date) return value;
  const stamp = value as { toDate?: () => Date } | null | undefined;
  return typeof stamp?.toDate === "function" ? stamp.toDate() : null;
}

export function toInboxItem(id: string, data: Record<string, unknown>): InboxItem | null {
  const notification = toNotification(data.notification);
  if (!notification) return null;
  return { id, notification, createdAt: dateOf(data.createdAt), readAt: dateOf(data.readAt) };
}
